// i18n-ns: budget (validation messages are resolved through the caller's t, not a local useTranslation)
import { BudgetScenarioType, trainingTotalCost, trainingWorkingDayCount } from '@callmart/types';
import { ScenarioInputsPayload } from '../../services/budgetScenario';
import { ScenarioBudgetAdjustmentResponse } from '../../services/budgetScenarioBudgetAdjustmentValues';
import { ScenarioContactValuesResponse } from '../../services/budgetScenarioContactValues';
import { ScenarioDriverRowsResponse } from '../../services/budgetScenarioDriverRows';
import { ScenarioMinutePriceValuesResponse } from '../../services/budgetScenarioMinutePriceValues';
import { ScenarioShiftRowsResponse } from '../../services/budgetScenarioShiftRows';

// The editable working copy of a scenario's type-specific inputs, and the pure functions around it
// (CALL-783). The five input editors used to save themselves — per cell, or per row behind a SPEICHERN
// button — so every type carried its own mutation hooks, its own race guards and its own idea of when a
// row was saveable. Now the whole page is ONE form: the editors report a draft upward, the page sends it
// with the metadata in a single PATCH, and Cancel reverts to the last saved state.
//
// Sibling of scenarioMeta.ts and deliberately free of React and of useTranslation: the draft is also
// what the tests reason about, and validation messages are resolved through the caller's `t`.

/** One monthly series of a draft row, keyed by first-of-month 'YYYY-MM-DD'. A cleared cell drops its
 *  key rather than storing null, so the map only ever holds values the user actually entered. */
export type MonthValues = Record<string, number>;

/** What every draft row has. Note there is NO server id: the payload is the desired final state and
 *  the server reconciles it, so a row only needs a local identity to be edited by. */
export interface ScenarioDraftRow {
    clientKey: string;
    monthValues: MonthValues;
}

/** A picked-entity row of the two entity-keyed types — a worktype for Contacts, a partner/location for
 *  Minute-price. `entityLabel` is the persisted label, kept so an entity the bucket no longer offers
 *  still renders with a name instead of a bare id. */
export interface ScenarioDraftEntity extends ScenarioDraftRow {
    entityId: number;
    entityLabel: string | null;
}

/** A Contacts row: the worktype's monthly deltas PLUS its training block (CALL-783 Stage 4), which the
 *  board's Kontakte grid shows in front of them — 10 agents × 8h × 24 days × €19 + €24 000 = €60 480.
 *
 *  The scalars sit on the entity, like the shift and driver rows carry theirs, so contacts is structurally
 *  the same thing as those two: a row of scalars plus a monthly series. Hence the same `number | ''` for
 *  each — a user must be able to clear a field, and '' coerces back to 0 on save. */
export interface ScenarioContactsDraftEntity extends ScenarioDraftEntity {
    agents: number | '';
    trainingDays: number | '';
    costPerHour: number | '';
    /** The board's "Trainerressource"; `trainerCost` is the persisted name, shared with the shift row. */
    trainerCost: number | '';
    /** The training allocation window (CALL-801), exactly like the shift row's — except that '' is a
     *  valid saved state here: the backend then walks from the scenario start, which is also what a new
     *  row is prefilled with. */
    trainingStart: string;
    trainingEnd: string;
    /** See ScenarioShiftDraftRow.trainingDaysPrefilled — the same one-directional prefill applies. */
    trainingDaysPrefilled?: boolean;
}

// Numeric fields allow '' so the user can clear them; they coerce to 0 on save. An id of '' means "not
// yet chosen" — for a freshly added row that is not an error, only a row that is not part of the payload
// yet (see draftToInputsPayload).
export interface ScenarioShiftDraftRow extends ScenarioDraftRow {
    /** The Working Unit whose volume moves (worktype – channel – task). Required once the row has any
     *  input: a Verschiebung is always within one working unit, so there is no "all" option (CALL-783). */
    workingUnitId: number | '';
    /** The persisted caption, kept so a working unit the bucket no longer offers still reads as a name. */
    workingUnitLabel: string | null;
    fromLocationId: number | '';
    toLocationId: number | '';
    agents: number | '';
    /** Training days per agent. The COST multiplier (trainingTotalCost), NOT the length of the
     *  Start→Ende window — see `trainingEnd`. Prefilled from that window while it is still untouched. */
    trainingDays: number | '';
    costPerHour: number | '';
    trainerCost: number | '';
    trainingStart: string; // '' = no training
    /** The ENTERED end of the training window (CALL-783); '' = none, and then the backend keeps walking
     *  `trainingDays` weekdays from the start. It decides which MONTHS the training cost lands in. */
    trainingEnd: string;
    /** Whether the `trainingDays` currently in this row was written by the Start/Ende prefill rather than
     *  typed by the user — the flag the prefill checks before overwriting it (see prefillTrainingDays).
     *
     *  A flag rather than "does trainingDays still equal the span's working days?": the two answers differ
     *  precisely when the user types the number the prefill would have produced, and then the implicit test
     *  would silently take his value back over. Transient and client-only — it is not in the payload, not
     *  in canonicalShiftRow (so it can never mark the form dirty), and a row arriving from the server has
     *  it unset, i.e. its stored number counts as typed and is never overwritten. */
    trainingDaysPrefilled?: boolean;
}

export interface ScenarioDriverDraftRow extends ScenarioDraftRow {
    worktypeId: number | '';
    worktypeName: string | null;
    wlPer1000: number | '';
    cpc: number | '';
    headsTraining: number | '';
    trainingDays: number | '';
    costPerHour: number | '';
    trainerResource: number | '';
    /** The training allocation window (CALL-801) — see ScenarioContactsDraftEntity. */
    trainingStart: string;
    trainingEnd: string;
    /** See ScenarioShiftDraftRow.trainingDaysPrefilled — the same one-directional prefill applies. */
    trainingDaysPrefilled?: boolean;
}

/** How every input grid reports an edit: a FUNCTION of the draft (or draft slice) it replaces, never a
 *  finished array.
 *
 *  ONE user gesture can change several cells — a paste, Delete over a selected range, Ctrl+D (copy range
 *  down), Ctrl+Z after a paste — and ag-grid dispatches the resulting `cellValueChanged` events
 *  ASYNCHRONOUSLY, batching the whole burst into a single `setTimeout(…, 0)` (LocalEventService's
 *  dispatchAsync/flushAsyncQueue). All N handlers therefore run back to back inside one macrotask with NO
 *  React render between them. Reporting a materialised array made each of them derive from the prop captured
 *  at the last render, i.e. from the same pre-burst state — so the last report won, React 18 coalesced the
 *  setState calls into one render, ag-grid delta-updated the rows by getRowId, and every other cell the user
 *  had just pasted (or cleared) visibly reverted (CALL-783).
 *
 *  An updater threads the burst instead: each handler is applied to the result of the previous one, exactly as
 *  `setState(prev => …)` composes. Everything a handler needs must come from that `prev` — a row lookup, a
 *  sibling field, a list of already-used ids taken from a prop is the same staleness one level down. */
export type DraftUpdater<TDraft> = (previous: TDraft) => TDraft;

/** The whole input set of one scenario type, discriminated exactly like the wire payload so the page
 *  can narrow once and hand the right slice to the right editor. */
export type ScenarioInputsDraft =
    | { type: BudgetScenarioType.CONTACTS; entities: ScenarioContactsDraftEntity[] }
    | { type: BudgetScenarioType.MINUTE_PRICE; entities: ScenarioDraftEntity[] }
    | { type: BudgetScenarioType.SHIFT; rows: ScenarioShiftDraftRow[] }
    | { type: BudgetScenarioType.BUDGET_ADJUSTMENT; monthValues: MonthValues; worktypeIds: number[] }
    | { type: BudgetScenarioType.REACHABILITY_ADJUSTMENT; rows: ScenarioDriverDraftRow[] };

/** What the form's `setInputs` accepts: a whole draft, or an updater of the one it currently holds.
 *
 *  Both, not just the updater: the scope multiselect commits a draft it has already computed (one gesture,
 *  one event, one render — nothing to thread), while every grid on the burst-capable cell path must hand up a
 *  `DraftUpdater`. The updater receives `null` too, because the inputs query resolves after the metadata one
 *  and a grid cannot assume there is a draft to derive from. */
export type ScenarioInputsUpdate =
    | ScenarioInputsDraft
    | ((previous: ScenarioInputsDraft | null) => ScenarioInputsDraft);

/** Each type's GET response — the draft is built from whichever one the active type fetched. */
export type ScenarioInputsData =
    | ScenarioContactValuesResponse
    | ScenarioMinutePriceValuesResponse
    | ScenarioShiftRowsResponse
    | ScenarioBudgetAdjustmentResponse
    | ScenarioDriverRowsResponse;

/** A blocking problem with the draft. `clientKey`/`entityId` locate the row it belongs to, so the page can
 *  scroll to it; without either it is a form-level problem.
 *
 *  `field` names the column whose CAPTION the row alert prefixes onto the message, and is therefore set only
 *  where the message does not already name its own field: "Working Unit: Working Unit ist erforderlich" is
 *  what setting it everywhere produced (CALL-784). The caption belongs to the grid — Kontakte labels its
 *  `trainerCost` column "Trainerressource €" while Verschiebung calls the same field "Trainerkosten" — so a
 *  message that needs one is prefixed there rather than built with a field name here, where it could only
 *  disagree with the column the user is looking at. */
export interface DraftIssue {
    clientKey?: string;
    entityId?: number;
    field?: string;
    message: string;
}

type Translate = (key: string, options?: Record<string, unknown>) => string;

/** Coerce a cleared or half-typed numeric field to the number the server will store. */
const num = (value: number | '' | null | undefined): number => (value === '' || value == null ? 0 : Number(value) || 0);

// Local row identities. A monotonic counter (rather than the array length) keeps a key unique even
// after rows in the middle have been removed — reusing one would make React and ag-grid conflate two
// different rows.
let clientKeySeq = 0;
export const newClientKey = (): string => `new-${(clientKeySeq += 1)}`;

/** The DOM anchor "Go to error" scrolls to for a flagged row.
 *
 *  It used to sit on the row's own card. Grid rows are virtualised, so there is no element guaranteed to
 *  exist per row any more — ScenarioRowGrid puts the id on the line of its warning alert that names the
 *  row instead, which is both always rendered and what the user needs to read. */
export const scenarioRowAnchorId = (clientKey: string): string => `scenario-row-${clientKey}`;

/** A draft row's monthly values as sorted, non-empty entries. Blank/cleared cells are skipped: the
 *  server stores a series, not a sparse grid, and `0` is a meaningful value while `''` is not. */
const monthEntries = (monthValues: MonthValues): Array<[string, number]> =>
    Object.entries(monthValues ?? {})
        .filter(([, value]) => value != null && (value as unknown) !== '' && !Number.isNaN(Number(value)))
        .map(([month, value]) => [month, Number(value)] as [string, number])
        .sort((a, b) => a[0].localeCompare(b[0]));

/** A row for an entity the scope multiselect just picked — no values yet, ready to be filled in. */
export const newEntityDraftRow = (entityId: number, entityLabel: string | null): ScenarioDraftEntity => ({
    clientKey: newClientKey(),
    entityId,
    entityLabel,
    monthValues: {},
});

/** The same, for Contacts: the worktype's training block starts zeroed rather than absent, so the grid's
 *  Trainingskosten column reads 0 € instead of blank and the cells are editable from the first click.
 *  `trainingStart` is prefilled with the scenario start (CALL-801) — the date the backend would walk
 *  from anyway when the field is empty, made visible and editable. */
export const newContactsDraftEntity = (
    entityId: number,
    entityLabel: string | null,
    trainingStart = ''
): ScenarioContactsDraftEntity => ({
    ...newEntityDraftRow(entityId, entityLabel),
    agents: 0,
    trainingDays: 0,
    costPerHour: 0,
    trainerCost: 0,
    trainingStart,
    trainingEnd: '',
});

/** A Shift row, either for a working unit the scope multiselect just picked or ('' ) a blank one from the
 *  grid's own ADD SHIFT. Von/Nach start empty in both cases: which locations are valid depends on the
 *  working unit (only the ones it has a billing config at can be priced), so the pair is chosen in the row.
 *  Unlike Driver this is NOT a bijection with the selection — one working unit may move to several targets,
 *  which is what keeps the add-row button. */
export const newShiftDraftRow = (
    workingUnitId: number | '',
    workingUnitLabel: string | null
): ScenarioShiftDraftRow => ({
    clientKey: newClientKey(),
    workingUnitId,
    workingUnitLabel,
    fromLocationId: '',
    toLocationId: '',
    agents: 0,
    trainingDays: 0,
    costPerHour: 0,
    trainerCost: 0,
    trainingStart: '',
    trainingEnd: '',
    monthValues: {},
});

/** A Driver row for a worktype the scope multiselect just picked. Driver rows are a bijection with that
 *  selection (the server has a UNIQUE (scenarioId, worktypeId) index), so selecting IS adding the row.
 *  `trainingStart` is prefilled with the scenario start (CALL-801), like the contacts entity. */
export const newDriverDraftRow = (
    worktypeId: number,
    worktypeName: string | null,
    trainingStart = ''
): ScenarioDriverDraftRow => ({
    clientKey: newClientKey(),
    worktypeId,
    worktypeName,
    wlPer1000: 0,
    cpc: 0,
    headsTraining: 0,
    trainingDays: 0,
    costPerHour: 0,
    trainerResource: 0,
    trainingStart,
    trainingEnd: '',
    monthValues: {},
});

/** The four training scalars a Contacts row carries. ONE list: it is both what makes such a row non-empty
 *  and what the server rejects as negative. */
const CONTACTS_TRAINING_FIELDS = ['agents', 'trainingDays', 'costPerHour', 'trainerCost'] as const;

/** True once either end of the training window is set — the fifth and sixth thing a training-carrying row
 *  can hold (CALL-801), and the one the "has the user entered anything?" predicates below used to miss. */
const hasTrainingWindow = (row: { trainingStart?: string; trainingEnd?: string }): boolean =>
    Boolean(row.trainingStart) || Boolean(row.trainingEnd);

/** True once an entity row holds data — an entered month, or (Contacts) any training input. Dropping the
 *  row would then discard something the user typed.
 *
 *  Deliberately written against the SUPERSET of both entity shapes instead of gaining a contacts-only twin:
 *  a Minute-price row simply has no training fields, `num(undefined)` is 0, and so one predicate answers for
 *  both types — which is what keeps the scope hook, the grids' trash and the Minute-price payload filter
 *  free of a per-type branch. */
export const entityRowHasValues = (row: ScenarioDraftEntity | ScenarioContactsDraftEntity): boolean =>
    monthEntries(row.monthValues).length > 0 ||
    CONTACTS_TRAINING_FIELDS.some((field) => num((row as Partial<ScenarioContactsDraftEntity>)[field]) !== 0) ||
    // The training WINDOW counts as entered data too (CALL-801): a Start/Ende typed before the scalars is
    // exactly as lost as they are when the worktype is deselected, so it must raise the same confirmation.
    hasTrainingWindow(row as Partial<ScenarioContactsDraftEntity>);

/** True once the user has put ANYTHING into a Shift row. A freshly added, still-blank row is not an
 *  error — it is simply not part of the payload yet (see draftToInputsPayload). */
export const shiftRowHasInput = (row: ScenarioShiftDraftRow): boolean =>
    row.workingUnitId !== '' ||
    row.fromLocationId !== '' ||
    row.toLocationId !== '' ||
    Boolean(row.trainingStart) ||
    Boolean(row.trainingEnd) ||
    num(row.agents) !== 0 ||
    num(row.trainingDays) !== 0 ||
    num(row.costPerHour) !== 0 ||
    num(row.trainerCost) !== 0 ||
    monthEntries(row.monthValues).length > 0;

/** True once a Driver row holds anything BEYOND its worktype — i.e. deselecting that worktype in the scope
 *  multiselect would discard entered data. `driverRowHasInput` cannot answer this: a row is only ever part
 *  of the selection because its worktype is set, which that predicate already counts as input. */
export const driverRowHasValues = (row: ScenarioDriverDraftRow): boolean =>
    hasTrainingWindow(row) ||
    num(row.wlPer1000) !== 0 ||
    num(row.cpc) !== 0 ||
    num(row.headsTraining) !== 0 ||
    num(row.trainingDays) !== 0 ||
    num(row.costPerHour) !== 0 ||
    num(row.trainerResource) !== 0 ||
    monthEntries(row.monthValues).length > 0;

/** The same rule for a Driver card. */
export const driverRowHasInput = (row: ScenarioDriverDraftRow): boolean =>
    row.worktypeId !== '' ||
    num(row.wlPer1000) !== 0 ||
    num(row.cpc) !== 0 ||
    num(row.headsTraining) !== 0 ||
    num(row.trainingDays) !== 0 ||
    num(row.costPerHour) !== 0 ||
    num(row.trainerResource) !== 0 ||
    monthEntries(row.monthValues).length > 0;

// Mirrors the backend rule (training inputs require a start date) so the user gets a field-level cue
// instead of a raw 400 toast after clicking Speichern. An entered `trainingEnd` counts as such an input,
// exactly as in refineShiftRow: on its own it is the allocation window of a cost with no start to be
// allocated from, so it would be stored and then ignored.
const missingTrainingStartFor = (row: ScenarioShiftDraftRow): boolean =>
    !row.trainingStart &&
    (num(row.agents) > 0 ||
        num(row.trainingDays) > 0 ||
        num(row.costPerHour) > 0 ||
        num(row.trainerCost) > 0 ||
        Boolean(row.trainingEnd));

// The other half of the pair, also mirroring refineShiftRow: an inverted window holds no working day, so
// the backend would silently book the whole training total into the start month. Both are 'YYYY-MM-DD',
// so a string compare IS a date compare. Typed against the window fields alone, because the driver row
// and the contacts entity carry the same pair since CALL-801 (see refineTrainingWindow backend-side —
// for those two an Ende without a Start is the other rejected shape, checked where they build issues).
const invertedTrainingWindowIn = (row: { trainingStart: string; trainingEnd: string }): boolean =>
    Boolean(row.trainingStart) && Boolean(row.trainingEnd) && row.trainingEnd < row.trainingStart;

// ── Derived row figures (the grids' read-only columns) ─────────
//
// The arithmetic itself lives in @callmart/types and is called by the backend impact calculation too, so a
// figure the grid shows cannot disagree with the money the server charges. Only the draft's '' → 0
// coercion belongs here, which is why these wrappers sit beside `num` rather than in the grids.
//
// Shift's "Ende" used to be one of them. It is an ENTERED date since CALL-783 — our weekday walk ignores
// public holidays and travel time, and the board shows Ende as a real date column — so the flow reversed:
// the date is typed and the DAY COUNT is what gets derived from it, as a prefill (see below).

/** Shift: "Trainingskosten" = Agents × 8h × Trainingstage × €/Stunde + Trainerkosten. */
export const shiftTrainingCost = (row: ScenarioShiftDraftRow): number =>
    trainingTotalCost(num(row.agents), num(row.trainingDays), num(row.costPerHour), num(row.trainerCost));

/** Driver: the same total, from its own field names (Heads Training, Trainerressource). */
export const driverTrainingCost = (row: ScenarioDriverDraftRow): number =>
    trainingTotalCost(num(row.headsTraining), num(row.trainingDays), num(row.costPerHour), num(row.trainerResource));

/** Contacts: the same total again — the board's Kontakte row, 10 × 8h × 24 × €19 + €24 000 = €60 480. */
export const contactsTrainingCost = (entity: ScenarioContactsDraftEntity): number =>
    trainingTotalCost(num(entity.agents), num(entity.trainingDays), num(entity.costPerHour), num(entity.trainerCost));

/** The training-window fields the prefill below couples — carried by the shift row, the driver row and
 *  the contacts entity alike (CALL-801). */
interface TrainingWindowRow extends ScenarioDraftRow {
    trainingDays: number | '';
    trainingStart: string;
    trainingEnd: string;
    trainingDaysPrefilled?: boolean;
}

/**
 * Fill `trainingDays` with the working-day count of the row's Start→Ende window (CALL-783).
 *
 * ONE-DIRECTIONAL and a prefill, not a lock:
 *  - only Start/Ende move Trainingstage. Editing Trainingstage never moves Ende — the two mean different
 *    things (days paid for vs. the window the cost is spread over), and a client who trains 15 of a
 *    22-working-day window has to be able to say so;
 *  - a number the USER typed is never overwritten. `trainingDaysPrefilled` is what tells the two apart:
 *    it is set here and cleared by patchTrainingRowField on any direct edit of the field. Deciding it by
 *    comparison instead ("is trainingDays still the span's count?") fails exactly when the user types the
 *    number the prefill would have produced — from then on his value would be re-derived behind him;
 *  - an empty or zero field is treated as "nothing to lose" and prefilled regardless of the flag, which
 *    is what makes the very first Start+Ende pair fill it (a fresh row starts at 0, unflagged).
 *
 * A window that holds no working day (Sat→Sun) or an incomplete/inverted pair prefills nothing: 0 days
 * would zero the training cost, which is a worse answer than leaving the field as it was.
 *
 * `holidays` are the national public-holiday dates the count subtracts besides weekends (CALL-801),
 * fetched by the page and threaded through the grid — the same set the backend allocation uses.
 * `undefined` is NOT "no holidays": it means the calendar has not arrived (or the request failed), and
 * nothing is prefilled then. An empty Set is the real "this window has none" answer. The difference
 * matters because a prefilled number is indistinguishable from one the user then adjusted, so a count
 * taken before the holidays landed could never be corrected — it would just be saved too high. The next
 * touch of either date (by then loaded) fills it.
 */
const prefillTrainingDays = <TRow extends TrainingWindowRow>(
    row: TRow,
    holidays: ReadonlySet<string> | undefined
): TRow => {
    if (holidays === undefined) return row;

    const isEmpty = row.trainingDays === '' || num(row.trainingDays) === 0;
    if (!isEmpty && !row.trainingDaysPrefilled) return row;

    const days = trainingWorkingDayCount(row.trainingStart || null, row.trainingEnd || null, holidays);
    if (days <= 0) return row;

    return { ...row, trainingDays: days, trainingDaysPrefilled: true };
};

/**
 * Patch one field of one training-carrying row (shift, driver or contacts), applying the Trainingstage
 * prefill (CALL-783; generalised from the shift row by CALL-801).
 *
 * The grids' edit handler instead of the generic `patchDraftRow`, because the training columns are
 * coupled: Start and Ende feed Trainingstage. Keeping that here rather than in the grids keeps it
 * testable and keeps the rule in one place — a grid only reports which cell changed.
 *
 * `holidays` is REQUIRED (though it may be undefined): the date edit itself always lands, but the
 * Trainingstage prefill only runs once the caller can say which days are holidays — see
 * prefillTrainingDays for why a missing calendar must not be read as "none".
 */
export const patchTrainingRowField = <TRow extends TrainingWindowRow>(
    rows: TRow[],
    clientKey: string,
    key: keyof TRow & string,
    value: number | string,
    holidays: ReadonlySet<string> | undefined
): TRow[] =>
    rows.map((row) => {
        if (row.clientKey !== clientKey) return row;
        const patched = { ...row, [key]: value } as TRow;
        // A direct edit makes the value the USER's, so the prefill must never touch it again.
        if (key === 'trainingDays') return { ...patched, trainingDaysPrefilled: false };
        if (key === 'trainingStart' || key === 'trainingEnd') return prefillTrainingDays(patched, holidays);
        return patched;
    });

// ── Building the draft from the server ─────────────────────────

/** Fold flattened cells into one draft row per entity. The label is taken from whichever cell carries
 *  one, so a row survives a value whose label came back null. */
const entitiesFromCells = (
    cells: Array<{ entityId: number; entityLabel: string | null; month: string; value: number }>
): ScenarioDraftEntity[] => {
    const byEntity = new Map<number, ScenarioDraftEntity>();
    for (const cell of cells) {
        let entity = byEntity.get(cell.entityId);
        if (!entity) {
            entity = {
                clientKey: `entity-${cell.entityId}`,
                entityId: cell.entityId,
                entityLabel: cell.entityLabel,
                monthValues: {},
            };
            byEntity.set(cell.entityId, entity);
        }
        if (cell.entityLabel) entity.entityLabel = cell.entityLabel;
        entity.monthValues[cell.month] = cell.value;
    }
    return [...byEntity.values()];
};

export const draftFromServer = (
    type: BudgetScenarioType | undefined,
    data: ScenarioInputsData | undefined
): ScenarioInputsDraft | null => {
    if (!type || !data) return null;
    switch (type) {
        case BudgetScenarioType.CONTACTS: {
            const { values, configs, availableWorktypes } = data as ScenarioContactValuesResponse;
            // The row set is the UNION of both lists by worktype, not just the worktypes that have deltas.
            // Deltas and training live in two tables and the grid shows them as one row, and either side may
            // name a worktype the other does not:
            //  - training entered before any delta — the backend's impact deliberately books that training
            //    cost on its own, so the row it belongs to has to be on screen;
            //  - a worktype that is merely SELECTED — the save writes a (zeroed) config row for every
            //    submitted entity, which is what finally makes the scope itself persistent. Rebuilding from
            //    `values` alone would make the row the user just picked vanish on the next refetch.
            const entities = new Map<number, ScenarioContactsDraftEntity>(
                entitiesFromCells(
                    (values ?? []).map((value) => ({
                        entityId: value.worktypeId,
                        entityLabel: value.worktypeName,
                        month: value.month,
                        value: value.contactsDelta,
                    }))
                ).map((entity) => [
                    entity.entityId,
                    // A worktype with deltas but no config row has no training configured — all zero.
                    {
                        ...entity,
                        agents: 0,
                        trainingDays: 0,
                        costPerHour: 0,
                        trainerCost: 0,
                        trainingStart: '',
                        trainingEnd: '',
                    },
                ])
            );
            for (const config of configs ?? []) {
                const withDeltas = entities.get(config.worktypeId);
                entities.set(config.worktypeId, {
                    ...(withDeltas ?? {
                        clientKey: `entity-${config.worktypeId}`,
                        entityId: config.worktypeId,
                        // A config carries no caption of its own, so a worktype with no delta row takes its
                        // name from the bucket's options. Null only where the bucket no longer offers it —
                        // the grid and the multiselect then fall back to "#id", still selectable.
                        entityLabel:
                            (availableWorktypes ?? []).find((option) => option.worktypeId === config.worktypeId)
                                ?.worktypeName ?? null,
                        monthValues: {},
                    }),
                    // Defaulted even though the server always sends all four: the same shape is built for a
                    // freshly picked row, which has no server entry at all.
                    agents: config.agents ?? 0,
                    trainingDays: config.trainingDays ?? 0,
                    costPerHour: config.costPerHour ?? 0,
                    trainerCost: config.trainerCost ?? 0,
                    trainingStart: config.trainingStart ?? '',
                    trainingEnd: config.trainingEnd ?? '',
                });
            }
            return { type, entities: [...entities.values()] };
        }
        case BudgetScenarioType.MINUTE_PRICE: {
            const { values } = data as ScenarioMinutePriceValuesResponse;
            return {
                type,
                entities: entitiesFromCells(
                    (values ?? []).map((value) => ({
                        entityId: value.locationId,
                        entityLabel: value.label,
                        month: value.month,
                        value: value.monthlyValue,
                    }))
                ),
            };
        }
        case BudgetScenarioType.SHIFT: {
            const { rows } = data as ScenarioShiftRowsResponse;
            return {
                type,
                rows: (rows ?? []).map((row) => ({
                    clientKey: `shift-${row.id}`,
                    workingUnitId: row.workingUnitId,
                    workingUnitLabel: row.workingUnitLabel,
                    fromLocationId: row.fromLocationId,
                    toLocationId: row.toLocationId,
                    agents: row.agents,
                    trainingDays: row.trainingDays,
                    costPerHour: row.costPerHour,
                    trainerCost: row.trainerCost,
                    trainingStart: row.trainingStart ?? '',
                    trainingEnd: row.trainingEnd ?? '',
                    // Deliberately NOT flagged as prefilled: a stored trainingDays is a number somebody
                    // decided on, so editing the window afterwards must leave it alone.
                    monthValues: Object.fromEntries(row.ramp.map((entry) => [entry.month, entry.sharePct])),
                })),
            };
        }
        case BudgetScenarioType.BUDGET_ADJUSTMENT: {
            const { values, scopeWorktypeIds } = data as ScenarioBudgetAdjustmentResponse;
            return {
                type,
                monthValues: Object.fromEntries((values ?? []).map((value) => [value.month, value.amount])),
                worktypeIds: [...(scopeWorktypeIds ?? [])],
            };
        }
        case BudgetScenarioType.REACHABILITY_ADJUSTMENT: {
            const { rows } = data as ScenarioDriverRowsResponse;
            return {
                type,
                rows: (rows ?? []).map((row) => ({
                    clientKey: `driver-${row.id}`,
                    worktypeId: row.worktypeId,
                    worktypeName: row.worktypeName,
                    wlPer1000: row.wlPer1000,
                    cpc: row.cpc,
                    headsTraining: row.headsTraining,
                    trainingDays: row.trainingDays,
                    costPerHour: row.costPerHour,
                    trainerResource: row.trainerResource,
                    trainingStart: row.trainingStart ?? '',
                    trainingEnd: row.trainingEnd ?? '',
                    // Like the shift row above: a stored trainingDays is a number somebody decided on, so
                    // it is deliberately NOT flagged as prefilled.
                    monthValues: Object.fromEntries(row.values.map((entry) => [entry.month, entry.driverDelta])),
                })),
            };
        }
        default:
            // A type whose input editor does not exist yet — the page saves metadata only.
            return null;
    }
};

// ── Turning the draft into the PATCH payload ───────────────────

// Every month a row holds is sent, NOT just the ones the current period shows: the backend replaces
// the whole series, so filtering to the visible window would permanently destroy persisted values
// outside a (temporarily) shortened period.
export const draftToInputsPayload = (draft: ScenarioInputsDraft): ScenarioInputsPayload => {
    switch (draft.type) {
        case BudgetScenarioType.CONTACTS:
            return {
                type: draft.type,
                // EVERY picked worktype is sent, even one with neither a delta nor a training input. The save
                // writes a config row per submitted entity, so this is what makes the scope selection itself
                // persistent — dropping the empty ones would make a worktype the user picked, saved and did
                // not fill in disappear on the next refetch. Minute-price cannot do the same: it has no
                // sibling table, so an entity with no values has nowhere to be stored.
                entities: draft.entities.map((entity) => ({
                    worktypeId: entity.entityId,
                    agents: num(entity.agents),
                    trainingDays: num(entity.trainingDays),
                    costPerHour: num(entity.costPerHour),
                    trainerCost: num(entity.trainerCost),
                    trainingStart: entity.trainingStart || null,
                    trainingEnd: entity.trainingEnd || null,
                    values: monthEntries(entity.monthValues).map(([month, contactsDelta]) => ({
                        month,
                        contactsDelta,
                    })),
                })),
            };
        case BudgetScenarioType.MINUTE_PRICE:
            return {
                type: draft.type,
                // Same rule, which for a Minute-price row means "has an entered month": it carries no
                // scalars of its own.
                entities: draft.entities.filter(entityRowHasValues).map((entity) => ({
                    locationId: entity.entityId,
                    values: monthEntries(entity.monthValues).map(([month, monthlyValue]) => ({
                        month,
                        monthlyValue,
                    })),
                })),
            };
        case BudgetScenarioType.SHIFT:
            return {
                type: draft.type,
                rows: draft.rows.filter(shiftRowHasInput).map((row) => ({
                    workingUnitId: num(row.workingUnitId),
                    fromLocationId: num(row.fromLocationId),
                    toLocationId: num(row.toLocationId),
                    agents: num(row.agents),
                    trainingDays: num(row.trainingDays),
                    costPerHour: num(row.costPerHour),
                    trainerCost: num(row.trainerCost),
                    trainingStart: row.trainingStart || null,
                    trainingEnd: row.trainingEnd || null,
                    ramp: monthEntries(row.monthValues).map(([month, sharePct]) => ({ month, sharePct })),
                })),
            };
        case BudgetScenarioType.BUDGET_ADJUSTMENT:
            return {
                type: draft.type,
                values: monthEntries(draft.monthValues).map(([month, amount]) => ({ month, amount })),
                worktypeIds: [...draft.worktypeIds],
            };
        case BudgetScenarioType.REACHABILITY_ADJUSTMENT:
            return {
                type: draft.type,
                rows: draft.rows.filter(driverRowHasInput).map((row) => ({
                    worktypeId: num(row.worktypeId),
                    wlPer1000: num(row.wlPer1000),
                    cpc: num(row.cpc),
                    headsTraining: num(row.headsTraining),
                    trainingDays: num(row.trainingDays),
                    costPerHour: num(row.costPerHour),
                    trainerResource: num(row.trainerResource),
                    trainingStart: row.trainingStart || null,
                    trainingEnd: row.trainingEnd || null,
                    values: monthEntries(row.monthValues).map(([month, driverDelta]) => ({ month, driverDelta })),
                })),
            };
    }
};

// ── Validation ─────────────────────────────────────────────────

// Every type with scalars rejects a negative one server-side. Since the save is all-or-nothing, one mistyped
// minus would 400 a request carrying every other edit on the page — so it is caught here, on the field.
const SHIFT_NON_NEGATIVE = ['agents', 'trainingDays', 'costPerHour', 'trainerCost'] as const;
const DRIVER_NON_NEGATIVE = [
    'wlPer1000',
    'cpc',
    'headsTraining',
    'trainingDays',
    'costPerHour',
    'trainerResource',
] as const;

const addNegativeIssues = <TRow extends ScenarioDraftRow>(
    row: TRow,
    keys: readonly (keyof TRow & string)[],
    t: Translate,
    issues: DraftIssue[]
) => {
    for (const key of keys) {
        if (num(row[key] as number | '') < 0) {
            issues.push({ clientKey: row.clientKey, field: key, message: t('validation.negativeNotAllowed') });
        }
    }
};

// Mirrors the rules the backend enforces, so the user gets a field-level cue on the offending row
// instead of a 400 toast with no location. A row the user has not touched yet is never flagged: it is
// dropped from the payload, and flagging it would deadlock Save the moment somebody adds a row and
// then edits a different one.
//
// Shift rows arrive by two paths, and only one of them produces such a pristine row (CALL-784):
//  - ADD SHIFT makes a row with NO working unit. shiftRowHasInput is false, draftToInputsPayload drops it,
//    and it is correctly silent until the user puts something in it.
//  - the scope multiselect makes a row that already carries its working unit, which shiftRowHasInput counts
//    as input — so that row IS submitted, and the backend requires Von/Nach. It has to be flagged: excluding
//    it from the payload instead would make the working unit the user just picked disappear on the next
//    refetch, because (unlike Kontakte, which persists a zeroed config row per selected worktype) a shift
//    has no table in which a selection alone can be stored.
// So the row created by picking a working unit is invalid on purpose. What CALL-784 removed is the noise:
// the message is shown once, on the row, and no longer repeated by both action bars.
const shiftIssues = (rows: ScenarioShiftDraftRow[], t: Translate): DraftIssue[] => {
    const issues: DraftIssue[] = [];
    for (const row of rows) {
        if (!shiftRowHasInput(row)) continue;
        addNegativeIssues(row, SHIFT_NON_NEGATIVE, t, issues);
        // A Verschiebung always moves volume within ONE working unit, so the column has no "all" option
        // and the server rejects a row without it (CALL-783). No `field`, like the pair below: the message
        // says "Working Unit ist erforderlich", so a caption prefix would only say it twice.
        if (row.workingUnitId === '') {
            issues.push({ clientKey: row.clientKey, message: t('validation.workingUnitRequired') });
        }
        if (row.fromLocationId === '' || row.toLocationId === '') {
            issues.push({ clientKey: row.clientKey, message: t('validation.shiftLocationsRequired') });
        } else if (row.fromLocationId === row.toLocationId) {
            // Mirrors refineShiftRow (CALL-794): Von === Nach moves the volume onto itself, both sides price
            // from the SAME config, so shiftCalc's savings term is exactly 0 while the row still books its
            // full training cost. The grid offers the pair without complaint — Von and Nach are built from
            // ONE option list and neither excludes the other side's value — so without this the row was
            // simply unsaveable, and unmarked with it. `else if` because two empty cells are equal too, and
            // the branch above already says what to do about those.
            issues.push({ clientKey: row.clientKey, message: t('validation.shiftLocationsEqual') });
        }
        if (missingTrainingStartFor(row)) {
            issues.push({
                clientKey: row.clientKey,
                field: 'trainingStart',
                message: t('validation.trainingStartRequired'),
            });
        }
        // Flagged on the Ende cell, which is the one the user can move to fix it — the start date may well
        // be the correct one of the two. The message names no field, so the row alert prints it verbatim.
        if (invertedTrainingWindowIn(row)) {
            issues.push({ clientKey: row.clientKey, message: t('validation.trainingEndBeforeStart') });
        }
        if (monthEntries(row.monthValues).some(([, sharePct]) => sharePct < 0 || sharePct > 100)) {
            issues.push({ clientKey: row.clientKey, message: t('validation.sharePctRange') });
        }
    }
    return issues;
};

// Mirrors refineTrainingWindow for the two types whose trainingStart is optional (CALL-801): only a
// window that cannot be anchored (an Ende without a Start) or an inverted one is a problem — an empty
// Start is valid and means "walk from the scenario start".
const trainingWindowIssues = (
    row: { clientKey: string; trainingStart: string; trainingEnd: string },
    t: Translate,
    issues: DraftIssue[]
) => {
    if (row.trainingEnd && !row.trainingStart) {
        issues.push({
            clientKey: row.clientKey,
            field: 'trainingStart',
            message: t('validation.trainingStartRequired'),
        });
    }
    if (invertedTrainingWindowIn(row)) {
        issues.push({ clientKey: row.clientKey, message: t('validation.trainingEndBeforeStart') });
    }
};

// Contacts' worktype comes from the scope multiselect, so it can be neither blank nor duplicated (each
// row's picker excludes what the other rows use), and a contact delta may be any number in any direction —
// leaving the sign check and the training window (CALL-801) as its only rules.
const contactsIssues = (entities: ScenarioContactsDraftEntity[], t: Translate): DraftIssue[] => {
    const issues: DraftIssue[] = [];
    for (const entity of entities) {
        // The window is checked on EVERY entity, not only the ones entityRowHasValues counts: that
        // predicate reads the numeric scalars and the months but not the dates, while the payload
        // submits every picked worktype — so an end-only or inverted window on an otherwise empty row
        // sailed through here and 400ed the whole form's save at refineTrainingWindow instead.
        trainingWindowIssues(entity, t, issues);
        // The sign check keeps the skip: an untouched row holds no number to be negative, and the
        // "dropped from the payload, so flagging it would deadlock Save" reasoning only ever applied
        // to the value fields anyway.
        if (!entityRowHasValues(entity)) continue;
        addNegativeIssues(entity, CONTACTS_TRAINING_FIELDS, t, issues);
    }
    return issues;
};

// One row per worktype (the server has a unique index on it), so a duplicate must be caught before the
// PATCH — the reconciling save would otherwise fail as a whole and lose the rest of the form's edits.
const driverIssues = (rows: ScenarioDriverDraftRow[], t: Translate): DraftIssue[] => {
    const issues: DraftIssue[] = [];
    const usedBy = new Set<number>();
    for (const row of rows) {
        if (!driverRowHasInput(row)) continue;
        addNegativeIssues(row, DRIVER_NON_NEGATIVE, t, issues);
        trainingWindowIssues(row, t, issues);
        // Both messages name the Worktype themselves, so neither carries a `field` to be prefixed with.
        if (row.worktypeId === '') {
            issues.push({ clientKey: row.clientKey, message: t('validation.worktypeRequired') });
            continue;
        }
        const worktypeId = Number(row.worktypeId);
        if (usedBy.has(worktypeId)) {
            issues.push({ clientKey: row.clientKey, message: t('validation.worktypeDuplicate') });
        } else {
            usedBy.add(worktypeId);
        }
    }
    return issues;
};

export const draftIssues = (draft: ScenarioInputsDraft | null, t: Translate): DraftIssue[] => {
    if (!draft) return [];
    switch (draft.type) {
        case BudgetScenarioType.CONTACTS:
            return contactsIssues(draft.entities, t);
        case BudgetScenarioType.SHIFT:
            return shiftIssues(draft.rows, t);
        case BudgetScenarioType.REACHABILITY_ADJUSTMENT:
            return driverIssues(draft.rows, t);
        default:
            // The minute-price grid and the budget-adjustment series accept any number in any cell.
            return [];
    }
};

// ── Dirty tracking ─────────────────────────────────────────────

/** Rows/entities as sorted JSON, so the comparison sees a set of rows rather than an array: the
 *  payload is reconciled server-side, so row ORDER carries no meaning and must not read as a change. */
const sortedRows = (rows: unknown[]): string[] => rows.map((row) => JSON.stringify(row)).sort();

// Positional arrays (not objects) so the field order is fixed by this literal instead of by whatever
// order the row's keys happen to have. The persisted labels are deliberately absent: a label refreshed
// from the server is not an edit.
// `trainingDaysPrefilled` is absent for the same reason the labels are: it is not sent, and how a number
// got into the field is not a change to the number.
const canonicalShiftRow = (row: ScenarioShiftDraftRow) => [
    row.workingUnitId === '' ? null : Number(row.workingUnitId),
    num(row.fromLocationId),
    num(row.toLocationId),
    num(row.agents),
    num(row.trainingDays),
    num(row.costPerHour),
    num(row.trainerCost),
    row.trainingStart || null,
    row.trainingEnd || null,
    monthEntries(row.monthValues),
];

const canonicalContactsEntity = (entity: ScenarioContactsDraftEntity) => [
    entity.entityId,
    num(entity.agents),
    num(entity.trainingDays),
    num(entity.costPerHour),
    num(entity.trainerCost),
    entity.trainingStart || null,
    entity.trainingEnd || null,
    monthEntries(entity.monthValues),
];

const canonicalDriverRow = (row: ScenarioDriverDraftRow) => [
    row.worktypeId === '' ? null : Number(row.worktypeId),
    num(row.wlPer1000),
    num(row.cpc),
    num(row.headsTraining),
    num(row.trainingDays),
    num(row.costPerHour),
    num(row.trainerResource),
    row.trainingStart || null,
    row.trainingEnd || null,
    monthEntries(row.monthValues),
];

/** A stable string identity of everything the draft would send.
 *
 *  Comparing drafts with a plain JSON.stringify does NOT work: `monthValues` is key-insertion-ordered,
 *  so clearing a cell and retyping the same number would move its key to the end and report the form as
 *  dirty forever. Month keys and rows are sorted here, and cleared cells drop out entirely, so equal
 *  content always yields an equal string.
 *
 *  It is the identity of the PAYLOAD, not of the draft object: every row draftToInputsPayload drops is
 *  dropped here too. A row the user added and has not filled in is therefore not a change — which is the fix
 *  for the mirror image of the CALL-784 validation noise: five empty Verschiebungen used to enable SPEICHERN,
 *  toast "erfolgreich gespeichert" and then be gone after the refetch, because the request they enabled
 *  carried nothing. They are not errors either (nobody has typed anything wrong), so the seam is here.
 *  useScenarioForm keeps such rows on screen across an adopt — see withPristineRows. */
export const canonicalDraft = (draft: ScenarioInputsDraft | null): string => {
    if (!draft) return '';
    switch (draft.type) {
        case BudgetScenarioType.CONTACTS:
            // No filter, unlike the four below: EVERY picked worktype is submitted (the save writes a zeroed
            // config row per entity), so picking one really is a change — it persists, and it must survive.
            // The training scalars are part of the identity too: editing only Agents must mark the form dirty.
            return JSON.stringify([draft.type, sortedRows(draft.entities.map(canonicalContactsEntity))]);
        case BudgetScenarioType.MINUTE_PRICE:
            return JSON.stringify([
                draft.type,
                sortedRows(
                    draft.entities
                        .filter(entityRowHasValues)
                        .map((entity) => [entity.entityId, monthEntries(entity.monthValues)])
                ),
            ]);
        case BudgetScenarioType.SHIFT:
            return JSON.stringify([draft.type, sortedRows(draft.rows.filter(shiftRowHasInput).map(canonicalShiftRow))]);
        case BudgetScenarioType.BUDGET_ADJUSTMENT:
            return JSON.stringify([
                draft.type,
                monthEntries(draft.monthValues),
                [...draft.worktypeIds].sort((a, b) => a - b),
            ]);
        case BudgetScenarioType.REACHABILITY_ADJUSTMENT:
            return JSON.stringify([
                draft.type,
                sortedRows(draft.rows.filter(driverRowHasInput).map(canonicalDriverRow)),
            ]);
    }
};

/** Server state, plus the pristine rows the current draft is holding open (CALL-784).
 *
 *  Those rows are not part of the payload and not part of `canonicalDraft`, so the form is clean while they
 *  are all that has changed — and the clean form adopts server state on every refetch. Without this, a save
 *  triggered by some OTHER edit (or any background refetch) made the empty rows the user had just added
 *  vanish under the cursor. The alternative — persisting them as real rows — is not open: the backend
 *  requires a `workingUnitId` on a shift row, and a minute-price selection has no table to live in at all.
 *
 *  Only the two shapes that can HAVE a pristine row are carried: shift's ADD SHIFT makes one with no working
 *  unit, and picking a partner–location makes a minute-price row with no values yet. A driver or contacts row
 *  is created by the multiselect and always carries its worktype. */
export const withPristineRows = (
    server: ScenarioInputsDraft | null,
    previous: ScenarioInputsDraft | null
): ScenarioInputsDraft | null => {
    if (!server || !previous || server.type !== previous.type) return server;
    switch (server.type) {
        case BudgetScenarioType.SHIFT: {
            const rows = (previous as Extract<ScenarioInputsDraft, { type: BudgetScenarioType.SHIFT }>).rows;
            const pristine = rows.filter((row) => !shiftRowHasInput(row));
            return pristine.length === 0 ? server : { ...server, rows: [...server.rows, ...pristine] };
        }
        case BudgetScenarioType.MINUTE_PRICE: {
            const entities = (previous as Extract<ScenarioInputsDraft, { type: BudgetScenarioType.MINUTE_PRICE }>)
                .entities;
            const pristine = entities.filter(
                (entity) =>
                    !entityRowHasValues(entity) && !server.entities.some((row) => row.entityId === entity.entityId)
            );
            return pristine.length === 0 ? server : { ...server, entities: [...server.entities, ...pristine] };
        }
        default:
            return server;
    }
};

// ── Row editing helpers (shared by the two row grids) ──────────

export const patchDraftRow = <TRow extends ScenarioDraftRow>(
    rows: TRow[],
    clientKey: string,
    patch: Partial<TRow>
): TRow[] => rows.map((row) => (row.clientKey === clientKey ? { ...row, ...patch } : row));

export const patchDraftMonthValue = <TRow extends ScenarioDraftRow>(
    rows: TRow[],
    clientKey: string,
    month: string,
    value: number | null
): TRow[] =>
    rows.map((row) => {
        if (row.clientKey !== clientKey) return row;
        const monthValues = { ...row.monthValues };
        if (value === null || Number.isNaN(value)) delete monthValues[month];
        else monthValues[month] = value;
        return { ...row, monthValues };
    });

export const removeDraftRow = <TRow extends ScenarioDraftRow>(rows: TRow[], clientKey: string): TRow[] =>
    rows.filter((row) => row.clientKey !== clientKey);
